import React, { useState, useEffect } from "react";
import Layout from '../form-layout'

// Note: this screen only records weight/height/BMI. It does NOT reject on a low BMI —
// the qualifying threshold depends on ethnicity (Q6) and comorbidities (Q8), both of
// which are collected later in the flow. The actual eligibility check runs once both
// are known — see src/lib/eligibility.js checkBmiEligibility(), called from the
// comorbidities screen (Q8f).
export default function Step6({ formData, setFormData, goToStep,onBack }) {
  const [weight, setWeight] = useState(formData.weight || "");
  const [height, setHeight] = useState(formData.height || "");
  const [weightUnit, setWeightUnit] = useState(formData.weightUnit || "kg");
  const [heightUnit, setHeightUnit] = useState(formData.heightUnit || "cm");
  const [bmi, setBmi] = useState(formData.bmi || null);

  useEffect(() => {
    if (formData.weight) setWeight(formData.weight);
    if (formData.height) setHeight(formData.height);
    if (formData.weightUnit) setWeightUnit(formData.weightUnit);
    if (formData.heightUnit) setHeightUnit(formData.heightUnit);
    if (formData.bmi) setBmi(formData.bmi);
  }, [formData]);

  useEffect(() => {
    if (weight && height) {
      const calculatedBmi = calculateBmi(weight, height, weightUnit, heightUnit);
      setBmi(calculatedBmi);
    }
  }, [weight, height, weightUnit, heightUnit]);

  const calculateBmi = (w, h, wUnit, hUnit) => {
    let weightKg = parseFloat(w);
    let heightCm = parseFloat(h);

    if (wUnit === "lbs") weightKg = weightKg / 2.20462;
    if (hUnit === "inches") heightCm = heightCm * 2.54;

    const heightM = heightCm / 100;
    return weightKg && heightM ? weightKg / (heightM * heightM) : null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!weight || !height) return;

    const calculatedBmi = calculateBmi(weight, height, weightUnit, heightUnit);

    setFormData({ ...formData, weight, height, bmi: calculatedBmi, weightUnit, heightUnit });
    localStorage.setItem("weight", weight);
    localStorage.setItem("height", height);
    localStorage.setItem("weightUnit", weightUnit);
    localStorage.setItem("heightUnit", heightUnit);
    localStorage.setItem("bmi", calculatedBmi.toFixed(2));

    goToStep("gender");
  };

  return (
    <Layout goToStep={goToStep} onBack={onBack}>
      <div className="flex justify-center items-center min-h-[70vh] bg-gradient-to-b to-white px-5 -mt-14">
        <div className="bg-white w-full max-w-md flex flex-col gap-6">
          <div className="text-left">
            <h2 className="text-sm sm:text-[14px] font-medium text-[#000000] text-center">
              What is your current weight and height?
            </h2>

            <p className="text-gray-600 mt-4 text-sm leading-relaxed">
              You are eligible for treatment if your BMI is 30 or above. Alternatively, a BMI of 27 or
              above if you have certain health conditions.
            </p>
            <p className="text-gray-600 mt-2 text-sm leading-relaxed">
              If you have already started weight-loss injection treatment, you can continue treatment
              until a minimum BMI of 22.
            </p>
            <p className="text-gray-700 mt-4 text-sm font-medium text-center">
              Please select either Metric or Imperial measurements
            </p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Weight Input */}
            <div className="flex gap-3">
              <input
                type="number"
                placeholder={`Weight (${weightUnit})`}
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="flex-1 border-2 border-sky-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-[#35C3D9]"
              />
              <select
                value={weightUnit}
                onChange={(e) => setWeightUnit(e.target.value)}
                className="flex-1 border-2 border-sky-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-[#35C3D9] bg-white"
              >
                <option value="kg">kg</option>
                <option value="lbs">lbs</option>
              </select>
            </div>

            {/* Height Input */}
            <div className="flex gap-3">
              <input
                type="number"
                placeholder={`Height (${heightUnit})`}
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                className="flex-1 border-2 border-sky-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-[#35C3D9]"
              />
              <select
                value={heightUnit}
                onChange={(e) => setHeightUnit(e.target.value)}
                className="flex-1 border-2 border-sky-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-[#35C3D9] bg-white"
              >
                <option value="cm">cm</option>
                <option value="inches">inches</option>
              </select>
            </div>


            {bmi && (
              <div className="text-center text-[#35C3D9] font-semibold text-lg">
                Your BMI: {bmi.toFixed(2)}
              </div>
            )}

            <button
              type="submit"
              disabled={!weight || !height} // disable if either weight or height is empty
              className={`w-full py-3 font-semibold rounded-xl transition-colors
    ${weight && height
                  ? "bg-[#35C3D9] text-white hover:bg-[#27AAC0]"
                  : "bg-gray-300 text-gray-600 cursor-not-allowed"
                }`}
            >
              Next →
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
